/**
 * KPI Tracker for performance monitoring
 * Tracks metrics against thresholds and reports violations
 */

export interface KPIThreshold {
  threshold: number;
  context?: Record<string, any>;
}

export interface KPIRecord {
  value: number;
  threshold: number;
  timestamp: number;
  context?: Record<string, any>;
  status: 'ok' | 'warning' | 'error';
}

class KPITracker {
  private kpis: Map<string, KPIRecord[]> = new Map();
  private maxHistorySize = 100;

  recordKPI(name: string, value: number, options: KPIThreshold): void {
    const { threshold, context } = options;
    const status = value <= threshold ? 'ok' : value <= threshold * 1.5 ? 'warning' : 'error';

    const record: KPIRecord = {
      value,
      threshold,
      timestamp: Date.now(),
      context,
      status,
    };

    if (!this.kpis.has(name)) {
      this.kpis.set(name, []);
    }

    const history = this.kpis.get(name)!;
    history.push(record);

    // Keep only recent history
    if (history.length > this.maxHistorySize) {
      history.shift();
    }

    // Log warnings/errors
    if (status === 'warning') {
      console.warn(`[KPI Warning] ${name}: ${value.toFixed(2)} (threshold: ${threshold})`, context);
    } else if (status === 'error') {
      console.error(`[KPI Error] ${name}: ${value.toFixed(2)} (threshold: ${threshold})`, context);
    }
  }

  getKPI(name: string): KPIRecord | null {
    const history = this.kpis.get(name);
    if (!history || history.length === 0) return null;
    return history[history.length - 1];
  }

  getKPIHistory(name: string, limit = 10): KPIRecord[] {
    const history = this.kpis.get(name) || [];
    return history.slice(-limit);
  }

  getAllKPIs(): Map<string, KPIRecord> {
    const result = new Map<string, KPIRecord>();
    this.kpis.forEach((history, name) => {
      if (history.length > 0) {
        result.set(name, history[history.length - 1]);
      }
    });
    return result;
  }

  clearKPI(name: string): void {
    this.kpis.delete(name);
  }

  clearAll(): void {
    this.kpis.clear();
  }
}

export const kpiTracker = new KPITracker();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).kpiTracker = kpiTracker;
}
